/**
 * RSA Key Management (Web Crypto API)
 * Asymmetric encryption for MasterKey sharing
 *
 * Security Parameters:
 * - Algorithm: RSA-OAEP-SHA256
 * - Key Size: 2048 bits
 * - Padding: OAEP (Optimal Asymmetric Encryption Padding)
 * - Hash: SHA-256
 *
 * Use Cases:
 * - User keypair generation (once per user)
 * - MasterKey encryption for sharing with other users
 * - Public key stored in plaintext (Benutzer.PublicKey)
 * - Private key encrypted with user password (Benutzer.EncryptedPrivateKey)
 */

const RSA_KEY_SIZE = 2048;
const RSA_ALGORITHM = {
  name: "RSA-OAEP",
  modulusLength: RSA_KEY_SIZE,
  publicExponent: new Uint8Array([1, 0, 1]), // 65537
  hash: "SHA-256",
};

/**
 * Generates RSA-2048 keypair for user
 *
 * Flow:
 * 1. Generate CryptoKey pair (Web Crypto API)
 * 2. Export public key to SPKI format (PEM)
 * 3. Export private key to PKCS8 format (PEM)
 * 4. Return both keys as PEM strings
 *
 * @returns {Promise<{publicKey: string, privateKey: string}>}
 */
export async function generateRSAKeypair() {
  try {
    // Step 1: Generate keypair
    const keypair = await window.crypto.subtle.generateKey(
      RSA_ALGORITHM,
      true, // extractable
      ["encrypt", "decrypt"]
    );

    // Step 2: Export public key (SPKI format)
    const publicKeyBuffer = await window.crypto.subtle.exportKey(
      "spki",
      keypair.publicKey
    );
    const publicKeyPem = arrayBufferToPem(publicKeyBuffer, "PUBLIC KEY");

    // Step 3: Export private key (PKCS8 format)
    const privateKeyBuffer = await window.crypto.subtle.exportKey(
      "pkcs8",
      keypair.privateKey
    );
    const privateKeyPem = arrayBufferToPem(privateKeyBuffer, "PRIVATE KEY");

    return {
      publicKey: publicKeyPem,
      privateKey: privateKeyPem,
    };
  } catch (error) {
    console.error("RSA keypair generation failed:", error);
    throw new Error("Failed to generate RSA keypair");
  }
}

/**
 * Encrypts MasterKey with recipient's public key
 *
 * Flow:
 * 1. Import recipient's public key (PEM → CryptoKey)
 * 2. Encrypt MasterKey bytes with RSA-OAEP
 * 3. Return encrypted MasterKey (Base64)
 *
 * @param {Uint8Array} masterKeyBytes - Raw MasterKey (32 bytes)
 * @param {string} recipientPublicKeyPem - Recipient's public key (PEM)
 * @returns {Promise<string>} - Encrypted MasterKey (Base64)
 */
export async function encryptMasterKeyWithPublicKey(
  masterKeyBytes,
  recipientPublicKeyPem
) {
  try {
    // Step 1: Import recipient's public key
    const publicKeyBuffer = pemToArrayBuffer(recipientPublicKeyPem);
    const publicKey = await window.crypto.subtle.importKey(
      "spki",
      publicKeyBuffer,
      RSA_ALGORITHM,
      false,
      ["encrypt"]
    );

    // Step 2: Encrypt MasterKey
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: "RSA-OAEP",
      },
      publicKey,
      masterKeyBytes
    );

    // Step 3: Return Base64
    return arrayBufferToBase64(new Uint8Array(encryptedBuffer));
  } catch (error) {
    console.error("MasterKey encryption failed:", error);
    throw new Error("Failed to encrypt MasterKey");
  }
}

/**
 * Decrypts MasterKey with user's private key
 *
 * Flow:
 * 1. Import user's private key (PEM → CryptoKey)
 * 2. Decrypt encrypted MasterKey with RSA-OAEP
 * 3. Return raw MasterKey bytes
 *
 * @param {string} encryptedMasterKeyBase64 - Encrypted MasterKey (Base64)
 * @param {string} privateKeyPem - User's private key (PEM, decrypted)
 * @returns {Promise<Uint8Array>} - Decrypted MasterKey (32 bytes)
 */
export async function decryptMasterKeyWithPrivateKey(
  encryptedMasterKeyBase64,
  privateKeyPem
) {
  try {
    // Step 1: Import private key
    const privateKeyBuffer = pemToArrayBuffer(privateKeyPem);
    const privateKey = await window.crypto.subtle.importKey(
      "pkcs8",
      privateKeyBuffer,
      RSA_ALGORITHM,
      false,
      ["decrypt"]
    );

    // Step 2: Decrypt MasterKey
    const encryptedBuffer = base64ToArrayBuffer(encryptedMasterKeyBase64);
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: "RSA-OAEP",
      },
      privateKey,
      encryptedBuffer
    );

    // Step 3: Return raw bytes
    return new Uint8Array(decryptedBuffer);
  } catch (error) {
    console.error("MasterKey decryption failed:", error);
    throw new Error("Failed to decrypt MasterKey");
  }
}

/**
 * Utility: ArrayBuffer to PEM string
 *
 * @param {ArrayBuffer} buffer - Key buffer
 * @param {string} label - PEM label ("PUBLIC KEY" or "PRIVATE KEY")
 * @returns {string} - PEM formatted string
 */
function arrayBufferToPem(buffer, label) {
  const base64 = arrayBufferToBase64(new Uint8Array(buffer));
  const pem = `-----BEGIN ${label}-----\n${base64.match(/.{1,64}/g).join("\n")}\n-----END ${label}-----`;
  return pem;
}

/**
 * Utility: PEM string to ArrayBuffer
 *
 * @param {string} pem - PEM formatted string
 * @returns {ArrayBuffer} - Key buffer
 */
function pemToArrayBuffer(pem) {
  const base64 = pem
    .replace(/-----BEGIN [A-Z ]+-----/, "")
    .replace(/-----END [A-Z ]+-----/, "")
    .replace(/\s/g, "");
  return base64ToArrayBuffer(base64);
}

/**
 * Utility: ArrayBuffer to Base64
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Utility: Base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
